import React from "react";
import { LineGraph } from "../../generated/core-api";
import styles from "./GraphViews.module.css";
import { Group } from "@visx/group";
import { LinePath } from "@visx/shape";
import { scaleLinear, scalePoint } from "@visx/scale";
import { AxisLeft, AxisBottom } from "@visx/axis";
import { GridRows, GridColumns } from "@visx/grid";
import { ParentSize } from "@visx/responsive";
import { GlyphCircle } from "@visx/glyph";
import { curveMonotoneX } from "@visx/curve";
import { useTooltip, TooltipWithBounds, defaultStyles } from "@visx/tooltip";
import { localPoint } from "@visx/event";

type LineGraphViewProps = {
  graph: LineGraph;
};

type TooltipData = {
  x: string;
  y: number;
  color: string;
  label: string;
};

const getX = (d: { x: string }) => d.x;
const getY = (d: { y: number }) => d.y;

// Generate a unique color for each dataset
const getColor = (index: number) => {
  const colors = [
    "#4f46e5",
    "#3b82f6",
    "#06b6d4",
    "#10b981",
    "#f59e0b",
    "#ef4444",
  ];
  return colors[index % colors.length];
};

const tooltipStyles = {
  ...defaultStyles,
  background: "rgba(255, 255, 255, 0.9)",
  border: "1px solid #e2e8f0",
  color: "#1a202c",
  boxShadow:
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
};

export const LineGraphView: React.FC<LineGraphViewProps> = ({ graph }) => {
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip,
  } = useTooltip<TooltipData>();

  // Extract data
  const labels = graph.labels;
  const datasets = graph.datasets;

  return (
    <div
      className={styles.graphContainer}
      style={{
        width: "100%",
        minWidth: "600px",
        boxSizing: "border-box",
        position: "relative",
      }}
    >
      <div className={styles.graphTitle}>Line Graph</div>

      <ParentSize>
        {({ width }) => {
          // Define dimensions
          const height = 300;
          const margin = { top: 40, right: 40, bottom: 80, left: 60 };
          const innerWidth = width - margin.left - margin.right;
          const innerHeight = height - margin.top - margin.bottom;

          // Format data for visx
          const formattedDatasets = datasets.map((dataset, datasetIndex) => {
            return dataset.data.map((value, index) => ({
              x: labels[index] || `Point ${index}`,
              y: value,
              datasetLabel: dataset.label || `Dataset ${datasetIndex + 1}`,
              datasetIndex,
            }));
          });

          // Calculate chart data ranges
          const allValues = datasets.flatMap((dataset) => dataset.data);
          const minValue = Math.min(0, ...allValues);
          const maxValue = Math.max(...allValues);

          // Define scales
          const xScale = scalePoint<string>({
            domain: labels,
            range: [0, innerWidth],
            padding: 0.5,
          });

          const yScale = scaleLinear<number>({
            domain: [minValue, maxValue * 1.1], // Add 10% padding on top
            range: [innerHeight, 0],
            nice: true,
          });

          // Handle tooltip interactions
          const handleMouseOver = (
            event: React.MouseEvent,
            point: { x: string; y: number },
            datasetLabel: string,
            color: string
          ) => {
            const eventSvgCoords = localPoint(event);
            if (!eventSvgCoords) return;

            showTooltip({
              tooltipData: {
                x: point.x,
                y: point.y,
                color,
                label: datasetLabel,
              },
              tooltipLeft: eventSvgCoords.x,
              tooltipTop: eventSvgCoords.y - 10, // Offset to position tooltip above pointer
            });
          };

          const handleTouchStart = (
            event: React.TouchEvent,
            point: { x: string; y: number },
            datasetLabel: string,
            color: string
          ) => {
            event.preventDefault();
            const touch = event.touches[0];
            showTooltip({
              tooltipData: {
                x: point.x,
                y: point.y,
                color,
                label: datasetLabel,
              },
              tooltipLeft: touch.clientX,
              tooltipTop: touch.clientY - 10,
            });
          };

          return (
            <svg width={width} height={height}>
              <Group left={margin.left} top={margin.top}>
                {/* Grid */}
                <GridRows
                  scale={yScale}
                  width={innerWidth}
                  stroke="#e0e0e0"
                  strokeDasharray="1,3"
                  strokeOpacity={0.6}
                />
                <GridColumns
                  scale={xScale}
                  height={innerHeight}
                  stroke="#e0e0e0"
                  strokeDasharray="1,3"
                  strokeOpacity={0.6}
                />

                {/* Axes */}
                <AxisLeft
                  scale={yScale}
                  tickStroke="#888888"
                  stroke="#888888"
                  tickLabelProps={() => ({
                    fill: "#6b7280",
                    fontSize: 12,
                    textAnchor: "end",
                    dx: -4,
                    dy: 4,
                  })}
                />
                <AxisBottom
                  top={innerHeight}
                  scale={xScale}
                  tickStroke="#888888"
                  stroke="#888888"
                  tickLabelProps={() => ({
                    fill: "#6b7280",
                    fontSize: 12,
                    textAnchor: "middle",
                    angle: 45,
                    dy: 10,
                    dx: 10,
                  })}
                />

                {/* Lines and Points */}
                {formattedDatasets.map((dataPoints, i) => {
                  const color = getColor(i);
                  const datasetLabel =
                    dataPoints[0]?.datasetLabel || `Dataset ${i + 1}`;

                  return (
                    <Group key={`dataset-${i}`}>
                      <LinePath
                        data={dataPoints}
                        x={(d) => xScale(getX(d)) || 0}
                        y={(d) => yScale(getY(d)) || 0}
                        stroke={color}
                        strokeWidth={2}
                        curve={curveMonotoneX}
                      />
                      {dataPoints.map((point, j) => (
                        <GlyphCircle
                          key={`point-${i}-${j}`}
                          left={xScale(getX(point)) || 0}
                          top={yScale(getY(point)) || 0}
                          size={24}
                          fill={color}
                          stroke="#fff"
                          strokeWidth={1}
                          onMouseEnter={(e) =>
                            handleMouseOver(e, point, datasetLabel, color)
                          }
                          onMouseLeave={hideTooltip}
                          onTouchStart={(e) =>
                            handleTouchStart(e, point, datasetLabel, color)
                          }
                        />
                      ))}
                    </Group>
                  );
                })}
              </Group>
            </svg>
          );
        }}
      </ParentSize>

      {/* Tooltip */}
      {tooltipOpen && tooltipData && (
        <TooltipWithBounds
          key={Math.random()}
          top={tooltipTop}
          left={tooltipLeft}
          style={tooltipStyles}
        >
          <div style={{ color: tooltipData.color, fontWeight: "bold" }}>
            {tooltipData.label}
          </div>
          <div>
            {tooltipData.x}: <strong>{tooltipData.y}</strong>
          </div>
        </TooltipWithBounds>
      )}

      {/* Legend */}
      <div className={styles.graphDescription}>
        {datasets.map((dataset, index) => (
          <div
            key={`legend-${index}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              margin: "0 10px",
            }}
          >
            <div
              style={{
                width: "12px",
                height: "12px",
                backgroundColor: getColor(index),
                marginRight: "5px",
                borderRadius: "2px",
              }}
            />
            <span>{dataset.label || `Dataset ${index + 1}`}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
